import { cn } from '@/lib/cn';

type OptimizedImageProps = {
  src: string;
  alt: string;
  className?: string;
  /** Eager + high fetch priority for LCP images. */
  priority?: boolean;
  sizes?: string;
} & (
  | { fill: true; width?: never; height?: never }
  | { fill?: false; width: number; height: number }
);

/**
 * Static-export friendly image using native `<picture>` so the browser loads
 * exactly one format (AVIF → WebP → original). Avoids next/image + picture
 * double-fetches that inflate LCP on mobile.
 */
export function OptimizedImage({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
  sizes = '100vw',
  fill = false,
}: OptimizedImageProps) {
  const isRemote = src.startsWith('data:') || src.startsWith('http');
  const lastDot = src.lastIndexOf('.');
  const base = !isRemote && lastDot > 0 ? src.slice(0, lastDot) : null;
  const intrinsicWidth = fill ? 1200 : width!;

  const imgClassName = cn(fill && 'absolute inset-0 h-full w-full', className);

  if (!base) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- remote/data URLs
      <img
        src={src}
        alt={alt}
        width={fill ? undefined : width}
        height={fill ? undefined : height}
        className={imgClassName}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        fetchPriority={priority ? 'high' : 'auto'}
      />
    );
  }

  // Priority (LCP) images ship a single 720w candidate so Slow 4G never
  // downloads both the mobile and full-resolution files.
  const avifSrcSet = priority
    ? `${base}-720.avif 720w`
    : `${base}-720.avif 720w, ${base}.avif ${intrinsicWidth}w`;
  const webpSrcSet = priority
    ? `${base}-720.webp 720w`
    : `${base}-720.webp 720w, ${base}.webp ${intrinsicWidth}w`;
  const fallbackSrc = priority ? `${base}-720.webp` : src;

  return (
    <picture className={cn(fill && 'contents')}>
      <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />
      <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
      <img
        src={fallbackSrc}
        alt={alt}
        width={fill ? undefined : width}
        height={fill ? undefined : height}
        className={imgClassName}
        sizes={sizes}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        fetchPriority={priority ? 'high' : 'auto'}
      />
    </picture>
  );
}
